/**
 * Created by Jerker Nord on 2021-01-27.
 */
import {NolimitAudioPlayer} from "@nolimitcity/slot-audio/bin/NolimitAudioPlayer";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {NolimitHowl} from "@nolimitcity/slot-audio/bin/NolimitHowl";
import {BenjiKIVWinConfigs, IWinRatioConfig} from "../configs/BenjiKIVWinConfigs";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {IFadeTrackConf} from "@nolimitcity/slot-audio/bin/INolimitAudioPlayer";
import {BenjiKIVGameMode} from "../server/BenjiKIVGameMode";
import {LoopableList} from "../utils/LoopableList";
import {gsap} from "gsap";

export class BenjiKIVSound {
    public static AS_WIN:string="asWin";
    public static AS_WIN_BIG:string="asWinBig";

    public static REELS_SPINNING:string = "reelsSpinning";
    public static REELS_LANDING:string[] = ["reelLanding1", "reelLanding2", "reelLanding3", "reelLanding4", "reelLanding5"];
    public static MAIN_GAME_MUSIC:string = "background-music-main";
    public static FS_INTRO_MUSIC:string = "fsIntro";
    public static FS_MUSIC:string = "fs";
    public static SFS_INTRO_MUSIC:string = "sfsIntro";
    public static SFS_MUSIC:string = "sfs";
    public static MULTIPLIER_COUNT_UP:LoopableList<string> = new LoopableList<string>([
        "multiplierCountup01",
        "multiplierCountup02",
        "multiplierCountup03"
    ]).setRandomizeAtWrapAround(true);
    public static MULTIPLIER_COUNT_UP_BIG:string = "multiplierCountupBig";
    public static MULTIPLIER_BIG_WIN_END:string = "multiplierBigwinEnd";
    public static COUNT_UP:string = "countup";
    public static COUNT_UP_END_TINY:string = "countupEndsTiny";
    public static COUNT_UP_END_SMALL:string = "countupEndsSmall";
    public static COUNT_UP_END_MEDIUM:string = "countupEndsMedium";
    public static COUNT_UP_END_LARGE:string = "countupEndsLarge";
    public static COUNT_UP_END_BAD_FS_AND_SFS:string = "countupEndsFSBad";
    public static COUNT_UP_END_GOOD_FS_AND_SFS:string = "countupEndsFSGood";
    public static BIG_WIN_MUSICS:string[] = ["bigwin1", "bigwin2", "bigwin3", "bigwin4", "bigwin5"];
    public static BIG_WIN_1_SOLO:string = "bigwin1Solo";
    public static BIG_WIN_MUSIC_END:string = "bigwinEnds";
    public static MAX_WIN_START:string = "capwinStart";
    public static MAX_WIN_MUSIC:string = "capwinMusic";
    public static INTRO_SPLASH:string = "introSplash";
    public static FS_AND_SFS_INTRO_START:string = "fsIntroStart";
    public static FS_AND_SFS_OUTRO:string = "fsFadeinOutro";
    public static FS_ENDS_GOOD:string = "fsEndsGood";
    public static FS_ENDS_BAD:string = "fsEndsBad";
    public static SFS_ENDS_GOOD:string = "fsSuperEndsGood";
    public static SFS_ENDS_BAD:string = "fsSuperEndsBad";
    public static WILD_FIRE:string = "wildNudgeFire";
    public static WILD_LANDING:string = "wildLanding";
    public static WILD_DEAD:string = "wildDead";
    public static MID_NUDGE_01:string = "midNudge1";
    public static MID_NUDGE_02:string = "midNudge2";
    public static RESPIN_SPLASH:string = "respinSplash";
    public static MULTI_SWEEP:string = "multipliersSweep";
    public static MULTI_UPGRADE:string = "multiplierUpgrade";
    public static MULTI_DOWNGRADE:string = "multiplierDowngrade";
    public static RING_GIRL_MULTI:string = "girlMultiplier";
    public static RING_GIRL_SPLIT:string = "girlXsplit";
    public static XSPLIT_ANTICIPATION:string = "xSplitAnticipation";
    public static XSPLIT_SLICE:string = "xSplitSlice";
    public static ENHANCER_RESULT:string = "topReelHighlight";
    public static BETLINE_HIGHLIGHT:string[] = ["betlineHighlightTinySmall", "betlineHighlightMedium", "betlineHighlightLarge"];
    public static NO_WIN_SOUNDS:string[] = ["noWinShit", "noWinBusted", "noWinShame"];
    public static SCATTER_LAND_SOUNDS:string[] = ["scatter1", "scatter2", "scatter3", "scatter4", "scatter5"];
    public static SCATTER_LAND_FS:string = "scatterLandingFS";
    public static SCATTER_REVEAL:string = "scatterReveal";
    public static SCATTER_WIN:string = "scatterWin";
    public static SCATTER_WIN_FS:string = "scatterWinFS";
    public static SCATTER_ANTICIPATION:string = "scatterAnticipation";
    public static SCATTER_INIT:string = "scatterHighlight";
    public static DEAD_PRESIDENT:LoopableList<string> = new LoopableList<string>([
        "deadPresident1",
        "deadPresident2",
        "deadPresident3"
    ]).setRandomizeAtWrapAround(true);
    public static WILD_NUDGE:LoopableList<string> = new LoopableList<string>([
        "wildNudge1",
        "wildNudge2",
        "wildNudge3"
    ]).setRandomizeAtWrapAround(true);

    public static CONTINUE_BUTTON:string = "continueButton";
    public static RED_XBET_ON:string = "xBetHigh";
    public static BLUE_XBET_ON:string = "xBetLow";
    public static XBET_OFF:string = "xBetOff";
    public static SFS_START:string = "fsSuperStart";
    public static SFS_PLUS_2:string = "fsSuperPlus2";
    public static DEAD_ANTICIPATION:string = "reelMidAnticipation";
    public static DICE_ANTICIPATION:string = "reelScatterAnticipation";

    private _currentMusic:string;
    private _countUpSound:NolimitHowl | undefined;
    private _player:NolimitAudioPlayer;
    private _previousSound:string;
    private _stopRepeatSoundTL:gsap.core.Timeline;
    private _stopRepeatSound:boolean;

    constructor() {
        this._player = <NolimitAudioPlayer>SlotGame.sound;
        this._stopRepeatSoundTL = gsap.timeline();
        this._stopRepeatSoundTL.add(() => {
            this._previousSound = "";
            this._stopRepeatSound = false;
        }, "+=0.01");
    }

    private static createConfig(name:string, volume?:number, repeat?:number) {
        return {
            name : name,
            volume : volume,
            repeat : repeat
        }
    }

    public playSound(name:string, volume?:number, repeat?:number):NolimitHowl | undefined {
        if(this._previousSound == name && this._stopRepeatSound == true) {
            return undefined;
        } else {
            this._previousSound = name;
            this._stopRepeatSound = true;
            this._stopRepeatSoundTL.restart();
            Logger.logDev("------- Play sound: " + name);
            return this._player.playTrack({name : name, volume : volume, repeat : repeat});
        }
    }

    public playNonLoopSound(name:string):void {
        Logger.logDev("------- Play non loop sound: " + name);
        this._player.playTrack({name : name})?.setLoop(false);
    }

    public fadeMusic(from:number, to:number, duration:number) {
        if(this._currentMusic != "") {
            const currentMusicHowl = this._player.fetchSoundPlayer(this._currentMusic);
            if(currentMusicHowl) {
                currentMusicHowl.fade(from, to, duration);
            } else {
                Logger.warn("Music not found for fading.");
            }
        }
    }

    // Use this to fade out sounds that are in effects folder
    public fadeOutEffect(fadeOutConf:IFadeTrackConf):void {
        const fade:NolimitHowl | undefined = this._player.fadeTrack(fadeOutConf);
        if(fade) {
            gsap.to(this, {
                duration : fadeOutConf.duration / 1000, onComplete : () => {
                    this._player.stopTrack(fadeOutConf.name);
                }
            });
        } else {
            this._player.stopTrack(fadeOutConf.name);
        }
    }

    public stopSound(name:string):NolimitHowl | undefined {
        return this._player.stopTrack(name);
    }

    public switchMusic(mode:BenjiKIVGameMode | string, fadeOutConf?:IFadeTrackConf, fadeInConf?:IFadeTrackConf) {
        let nextMusic = "";

        switch(mode) {
            case BenjiKIVSound.SFS_INTRO_MUSIC:
                nextMusic = BenjiKIVSound.SFS_INTRO_MUSIC;
                break;
            case BenjiKIVSound.FS_INTRO_MUSIC:
                nextMusic = BenjiKIVSound.FS_INTRO_MUSIC;
                break;
            case BenjiKIVGameMode.FREESPIN:
                nextMusic = BenjiKIVSound.FS_MUSIC;
                break;
            case BenjiKIVGameMode.SUPER_FREESPIN:
                nextMusic = BenjiKIVSound.SFS_MUSIC;
                break;
            default:
                nextMusic = BenjiKIVSound.MAIN_GAME_MUSIC;
                break;
        }

        if(nextMusic == this._currentMusic) {
            return;
        }

        if(this._currentMusic !== null) {
            const fadeName:string = this._currentMusic;
            if(fadeOutConf) {
                fadeOutConf.name = this._currentMusic;
                const fade:NolimitHowl | undefined = this._player.fadeTrack(fadeOutConf);
                if(fade) {
                    gsap.to(this, {
                        duration : fadeOutConf.duration / 1000, onComplete : () => {
                            this._player.stopTrack(fadeName);
                        }
                    });
                } else {
                    this._player.stopTrack(fadeName);
                }
            } else {
                this._player.stopTrack(fadeName);
            }
        }

        Logger.logDev("------- Switching sound from: " + this._currentMusic + " to: " + nextMusic);

        this._player.playTrack({name : nextMusic, volume : 1});
        if(fadeInConf) {
            fadeInConf.name = nextMusic;
            this._player.fadeTrack(fadeInConf);
        }
        this._currentMusic = nextMusic;
    }

    public playRandomDeadPresident():void {
        this.playSound(BenjiKIVSound.DEAD_PRESIDENT.next());
    }

    public playRandomWildNudge():void {
        this.playSound(BenjiKIVSound.WILD_NUDGE.next());
    }

    public startCountUp() {
        this._countUpSound = this.playSound(BenjiKIVSound.COUNT_UP);
        if(this._countUpSound) {
            this._countUpSound.setLoop(true);
        }
    }

    public stopCountUp(config?:IWinRatioConfig, playEndSound:boolean = true) {
        if(this._countUpSound && this._countUpSound.isPlaying) {
            this._countUpSound.stop();
        }

        if(playEndSound) {
            let sound = BenjiKIVSound.COUNT_UP_END_LARGE;

            if(config) {
                if(config.type == BenjiKIVWinConfigs.TINY) {
                    sound = BenjiKIVSound.COUNT_UP_END_TINY;
                } else if(config.type == BenjiKIVWinConfigs.SMALL) {
                    sound = BenjiKIVSound.COUNT_UP_END_SMALL;
                } else if(config.type == BenjiKIVWinConfigs.MEDIUM) {
                    sound = BenjiKIVSound.COUNT_UP_END_MEDIUM;
                } else if(config.type == BenjiKIVWinConfigs.BIG_WIN_06) {
                    sound = "";
                }
            }

            this.playSound(sound);
        }
    }
}